import { useContext, useMemo, useState } from 'react';
import {
  useApplicationDetail,
  email_communication_hooks,
} from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { email } from '@axo/shared/data-access/types';
import { communication_template_hooks } from '@axo/shared/data-access/hooks';

interface IEmailNew {
  email: email.CreateEmail;
  isNew: true;
}

interface IEmailOld {
  email: email.Email;
  isNew: false;
}

type IEmailState = IEmailNew | IEmailOld;

export function useEmailState(applicationID: string) {
  const { state } = useContext(DataAccessContext);
  const application = useApplicationDetail(applicationID);
  const emails = email_communication_hooks.useEmails(applicationID);
  const [emailsState, setEmailsState] = useState<IEmailState[]>([]);
  const [currentLoanApplication, setCurrentLoanApplication] = useState('');
  const createEmail =
    email_communication_hooks.useCreateCommunicationEmail(applicationID);
  const fillTemplate =
    communication_template_hooks.useFillCommunicationTemplate();

  const initialEmail: email.CreateEmail = {
    Subject: '',
    Body: '',
    LoanApplicationID: applicationID,
    MarketCountry: application.data?.MarketCountry || '',
    CreatorID: state.user.clientID,
  };

  const createInitialEmailState = (email: email.Email[]) => {
    return email.map((email) => ({
      email: email,
      isNew: false,
    }));
  };

  useMemo(() => {
    if (emails.data && currentLoanApplication !== applicationID) {
      setEmailsState(createInitialEmailState(emails.data.Items));
      setCurrentLoanApplication(applicationID);
    }
  }, [
    setEmailsState,
    emails,
    currentLoanApplication,
    setCurrentLoanApplication,
    applicationID,
  ]);

  const updateEmailsState = (
    compare: keyof IEmailState,
    originalState: IEmailState,
    updatedState: IEmailState
  ) => {
    setEmailsState((prevState) =>
      prevState.map((email) => {
        if (email[compare] === originalState[compare]) {
          return updatedState;
        } else {
          return email;
        }
      })
    );
  };

  return {
    state: emailsState,
    hasLoaded: currentLoanApplication === applicationID,
    addNewEmail: (template: email.EmailTemplate) => {
      setEmailsState((prevState) => [
        {
          email: {
            ...initialEmail,
            ID: new Date().toISOString(),
            Subject: template.Subject,
            Body: template.Body,
            TemplateID: template.ID,
          },
          isNew: true,
        },
        ...prevState,
      ]);
    },
    saveEmail: (email: IEmailState) => {
      if (email.isNew) {
        fillTemplate
          .mutateAsync({
            LoanApplicationID: applicationID,
            LoanQuoteID: email.email.LoanQuoteID ?? '',
            Body: email.email.Body,
          })
          .then((response) => {
            const toCreate: email.CreateEmail = {
              ...email.email,
              Body: response.Body,
            };
            createEmail.mutateAsync(toCreate).then((updated) =>
              updateEmailsState('email', email, {
                email: updated,
                isNew: false,
              })
            );
          });
      }
    },
    cancelEmail: (email: IEmailState) => {
      if (email.isNew) {
        setEmailsState((prevState) =>
          prevState.filter((currentEmail) => currentEmail !== email)
        );
      }
    },
    editEmail: (email: IEmailState) => (editEmail: email.CreateEmail) => {
      if (email.isNew) {
        updateEmailsState('email', email, {
          ...email,
          email: editEmail,
        });
      }
    },
  };
}
